<template>
  <div id="gatewayOverview">
    <div class="row">
      <div class="col-lg-4">
        <properties :gateway="gateway" :last-statistics="lastStatistics" />
      </div>
      <div class="col-lg-8">
        <position :gateway="gateway" :last-statistics="lastStatistics" />
      </div>
    </div>

    <div class="row">
      <div class="col-lg-4">
        <health :gateway="gateway" />
      </div>
      <div class="col-lg-4">
        <last-events id="gatewayLastAlarmsGrid"
                     :title="$t('g-lastEvents:lastAlarms')"
                     :events="alarms"
                     @update="getLastEvents()"
        />
      </div>
      <div class="col-lg-4">
        <last-events id="gatewayLastNotificationsGrid"
                     :title="$t('g-lastEvents:lastNotifications')"
                     :events="notifications"
                     @update="getLastEvents()"
        />
      </div>
    </div>

    <div v-if="$store.getters.gatewayFeature(gateway, 'modemsManagement')" class="row">
      <div class="col-lg-12">
        <modems :gateway="gateway" />
      </div>
    </div>

    <floating-button v-if="isRole.admin" :click-handler="editGateway" icon-classes="icon-edit" />
    <gateway-form v-model="showGatewayForm" :gateway-to-edit="gatewayToEdit" @success="getGateway" />
  </div>
</template>

<script>
import { isEmpty, map, orderBy, unionWith, filter } from 'lodash-es'
import { mapGetters } from 'vuex'
import constants from '@/configuration/constants'
import to from '@/modules/await-to-js'

import Properties from './Properties'
import Position from './Position'
import LastEvents from './LastEvents'
import Health from './Health'
import Modems from './Modems'

import GatewayForm from './GatewayForm'

export default {
  titleKey: 'gatewayOverview',

  components: { Properties, Position, LastEvents, Health, Modems, GatewayForm },

  props: {
    eui: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      alarms: [],
      notifications: [],
      lastStatistics: [],
      // Edit
      showGatewayForm: false,
      gatewayToEdit: {}
    }
  },

  computed: {
    ...mapGetters(['gateway', 'gatewayHealthRefreshFreq'])
  },

  watch: {
    eui () {
      // Reset data
      this.lastEvents = []
    },

    gateway () {
      // update the selection after refreshing the gateway
      if (this.$store.getters.isGatewayInSelection(this.gateway)) {
        this.$store.commit('removeGatewayFromSelection', this.gateway)
        this.$store.commit('addGatewayInSelection', this.gateway)
      }

      // get relatives data
      this.getLastEvents()
      this.getGatewayLastStatistics()
    },

    showGatewayForm () {
      // Empty form when hidding modal
      if (!this.showGatewayForm) this.gatewayToEdit = {}
    }
  },

  created () {
    this.getLastEvents()
    this.getGatewayLastStatistics()
  },

  methods: {
    getGateway () {
      return this.$store.dispatch('getGateway', this.eui)
    },

    // Get nb satellites and last known position
    async getGatewayLastStatistics () {
      if (isEmpty(this.gateway)) return

      let error, response
      [error, response] = await to(this.$api.getGatewayLastStatistics(this.gateway, { fields: '!gateway,!links' }))
      if (error || !response) return

      this.lastStatistics = response.data.list
    },

    getLastEvents () {
      if (isEmpty(this.gateway)) return

      Promise.all([
        this.getLastGatewayEvents(),
        this.getLastNetworkEvents()
      ])
        .then(([gatewayEventsRep, networkEventsRep]) => {
          // Network events as time but no date...
          const networkEvents = map(networkEventsRep.data.list, event => { return { ...event, date: event.time } })
          // Aggregate all events and order by newest first
          const events = orderBy(unionWith(gatewayEventsRep.data.list, networkEvents), ['date'], ['desc'])
          // Get last alarms
          this.alarms = filter(events, event => event.type === 'ALARM').slice(0, constants.overviewNbEvents)
          // Get last notifications
          this.notifications = filter(events, event => event.type === 'NOTIFICATION').slice(0, constants.overviewNbEvents)
        })
    },

    async getLastGatewayEvents () {
      const search = {
        operator: 'AND',
        conditions: [
          { operation: 'eq', values: ['BSC'], operand: 'domain' },
          { operation: 'eq', values: ['false'], operand: 'read' }
        ]
      }

      const params = { page: 1, pageSize: constants.overviewNbEvents * 2, sort: '-date', search: JSON.stringify(search) }
      return this.$api.getGatewayLastEvents(this.gateway, params)
    },

    getLastNetworkEvents () {
      const search = {
        operator: 'AND',
        conditions: [
          { operation: 'eq', values: [this.gateway.eui], operand: 'eui' },
          { operation: 'eq', values: ['false'], operand: 'read' }
        ]
      }

      const params = { page: 1, pageSize: constants.overviewNbEvents * 2, sort: '-time', search: JSON.stringify(search) }
      return this.$api.getRncGatewaysLastEvents(this.$store.getters.application, params)
    },

    editGateway () {
      this.gatewayToEdit = this.gateway
      this.showGatewayForm = true
    }
  }
}
</script>
